import { Fragment } from 'react';
import { useFormikContext } from 'formik';
import { Input } from '@crazyegginc/hatch';

import { CurrencySelector } from '/src/components/CurrencySelector';

import { AUDIENCE_METADATA } from '../../../metadata';
import { ConditionTooltip } from '../ConditionTooltip';

export function MoneyValue({ index, innerIndex }) {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = useFormikContext();
  const condition = values.conditions[index];
  const { criteria, rows } = condition;
  const row = rows[innerIndex];
  const valuePath = `conditions[${index}].rows[${innerIndex}].values`;
  const isBetween = row.comparison === 'between';
  const showTooltip = row.tooltip === 'value';

  const metadata = AUDIENCE_METADATA[criteria];
  const rowValues = row.values;
  const rowTouched = touched.conditions?.[index]?.rows?.[innerIndex]?.values;
  const rowError = errors.conditions?.[index]?.rows?.[innerIndex]?.values;

  function onTooltipDismiss() {
    setFieldValue(`conditions[${index}].rows[${innerIndex}].tooltip`, false, false);
  }

  return (
    <div className="flex flex-wrap gap-x-2.5 gap-y-2.5">
      {(isBetween ? ['amount', 'max'] : ['amount']).map((fieldName, i, fields) => {
        const isMax = fieldName === 'max';
        const fieldError = getVisibleAmountError({ rowTouched, rowError, isBetween, fieldName });
        const input = (
          <div className="w-[80px]">
            <Input
              id={`${valuePath}.${fieldName}`}
              name={`${valuePath}.${fieldName}`}
              value={rowValues[fieldName]}
              placeholder={isBetween ? (isMax ? 'Max' : 'Min') : metadata.placeholder}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-label={isBetween ? `${criteria} ${isMax ? 'maximum' : 'minimum'}` : criteria}
              className="!pl-2.5"
              error={fieldError}
            />
          </div>
        );

        return (
          <Fragment key={fieldName}>
            <ConditionTooltip
              criteria={metadata}
              show={showTooltip && i === fields.length - 1}
              onDismiss={onTooltipDismiss}
            >
              {input}
            </ConditionTooltip>
            {isBetween && i === 0 ? <div className="flex h-[35px] items-center">and</div> : null}
          </Fragment>
        );
      })}
      <div className="w-[100px]">
        <CurrencySelector
          value={rowValues.currency}
          onSelect={(value) => setFieldValue(`${valuePath}.currency`, value)}
          unitOnly={true}
          aria-label={`${criteria} currency`}
          error={getCurrencyError({ rowTouched, rowError })}
          buttonClassName="!pl-2.5"
        />
      </div>
    </div>
  );
}

function getVisibleAmountError({ rowTouched, rowError, isBetween, fieldName }) {
  if (!rowTouched || !rowError) return null;

  if (!isBetween) {
    return typeof rowError === 'string' ? rowError : (rowError.amount ?? null);
  }

  if (typeof rowError === 'string') {
    return fieldName === 'amount' ? rowError : null;
  }

  const minError = rowError.amount ?? null;
  const maxError = rowError.max ?? null;

  // Show one range error at a time to avoid overlapping messages in the UI.
  if (minError) {
    return fieldName === 'amount' ? minError : null;
  }

  if (maxError) {
    return fieldName === 'max' ? maxError : null;
  }

  return null;
}

function getCurrencyError({ rowTouched, rowError }) {
  if (!rowTouched || !rowError || typeof rowError === 'string') return null;

  return rowError.currency ?? null;
}
